/**
 * Admin Messages Dashboard
 * Central hub for viewing and managing all admin direct messages
 *
 * Conversations come from the public.admin_conversations view (one row per user, computed in the
 * database). Until that view exists the page falls back to grouping raw messages in the browser,
 * the way it always did. Realtime changes refresh the list in the background, debounced, without
 * swapping the page to a loading screen; the open chat and the scroll position stay where they are.
 */

import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card'
import { Input } from '@/components/admin/ui/input'
import { Badge } from '@/components/admin/ui/badge'
import { ScrollArea } from '@/components/admin/ui/scroll-area'
import { supabaseAdmin } from '@/integrations/supabase/client'
import { MessageSquare, Search, User, Clock, CheckCheck, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import AdminChatPanel from '@/components/admin/AdminChatPanel'
import {
  sortConversations, filterConversations, summarize, ilikePattern, groupMessages, SORTS, isRoutedTo, withPersonalUnread
} from '@/lib/messages/conversations.js'

interface UserConversation {
  user_id: string
  user_name: string | null
  user_email: string | null
  user_role: string | null
  last_message_at: string
  unread_count: number
  message_count: number
  last_message_preview: string | null
}

const SORT_STORAGE_KEY = 'lp-console.messages.sort'
const REFETCH_DEBOUNCE_MS = 1500
const SEARCH_DEBOUNCE_MS = 350
const SEARCH_MIN_CHARS = 2

// PGRST205 / 42P01: the view is not created yet (migration not run); use the legacy path.
const isViewMissing = (error: { code?: string } | null) =>
  error?.code === 'PGRST205' || error?.code === '42P01'

function readStoredSort(): string {
  try {
    const v = localStorage.getItem(SORT_STORAGE_KEY)
    return SORTS.some((s: { key: string }) => s.key === v) ? (v as string) : 'unread_first'
  } catch {
    return 'unread_first'
  }
}

const ROUTE_LABEL: Record<string, string> = { admin: 'Admin', sales: 'Sales', billing: 'Billing', editing: 'Editing', talent_coordinator: 'Talent' }

/** The new console's per-person reads (Traci's doc, Oct 2026): my unread counts, and the conversations my department started. */
export type PersonalMessages = {
  load: () => Promise<{ installed: boolean; unread: Record<string, number> | null; started: string[] | null }>
  markRead: (userId: string) => Promise<unknown>
}

/**
 * `audiences` and `split` are optional and only the new console passes them. `audiences` scopes by role; `split` is
 * the prototype's two-pane inbox (v108 §6): the list on the left, the open thread beside it. Without them the page is
 * exactly the current console's Messages: every conversation, no Clients / Talent chips, the chat as a pop-up.
 * `routeTags` (conversation user id → departments it is tagged to) shows the routing pills; `routedTo` keeps only the
 * conversations routed to that department (Billing, Editing: v109 §8); `paneHeader` renders above the open thread.
 * `personal` swaps the shared unread counts for this person's own and marks a conversation read for them when opened.
 */
export default function Messages({ audiences, split = false, routeTags, routedTo = null, paneHeader, personal, chatProps }: { audiences?: Array<'client' | 'talent'>; split?: boolean; routeTags?: Record<string, string[]>; routedTo?: string | null; paneHeader?: (u: { id: string; name: string; role: string }) => ReactNode; personal?: PersonalMessages; chatProps?: { onPickMessage?: (m: { id: string; text: string; from: string; at: string; userId?: string }) => void; highlightId?: string | null; markers?: Record<string, Array<{ label: string; why?: string | null }>> } } = {}) {
  const [conversations, setConversations] = useState<UserConversation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [source, setSource] = useState<'view' | 'messages'>('view')
  const [searchQuery, setSearchQuery] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [kind, setKind] = useState<'all' | 'client' | 'talent'>('all')
  const [sort, setSort] = useState<string>(readStoredSort)
  const [remoteMatches, setRemoteMatches] = useState<Set<string> | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string; role: string } | null>(null)

  const [started, setStarted] = useState<Set<string> | null>(null)
  const personalRef = useRef(personal)
  personalRef.current = personal
  const openRef = useRef<string | null>(null)
  openRef.current = selectedUser?.id || null

  const inFlight = useRef(false)
  const pending = useRef(false)
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadFromView = async (): Promise<UserConversation[]> => {
    const { data, error } = await supabaseAdmin
      .from('admin_conversations')
      .select('user_id, user_name, user_email, user_role, last_message_at, unread_count, message_count, last_message_preview')
      .order('last_message_at', { ascending: false })
    if (error) throw error
    return (data || []) as UserConversation[]
  }

  // Legacy path: every message, grouped in the browser. Only until the view migration is run.
  const loadFromMessages = async (): Promise<UserConversation[]> => {
    const { data, error } = await supabaseAdmin
      .from('admin_messages')
      .select(`
        id,
        user_id,
        sender_id,
        message_text,
        created_at,
        is_read_admin,
        user:users!admin_messages_user_id_fkey(
          id,
          full_name,
          email,
          role
        )
      `)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
    if (error) throw error
    return groupMessages(data || []) as UserConversation[]
  }

  // Only the first load shows the loading screen. Every later call refreshes in the background;
  // a call that arrives while one is running marks it pending and runs once more afterwards.
  const fetchConversations = useCallback(async (initial = false) => {
    if (inFlight.current) {
      pending.current = true
      return
    }
    inFlight.current = true
    if (initial) setIsLoading(true)
    else setIsRefreshing(true)
    try {
      let rows: UserConversation[]
      try {
        rows = await loadFromView()
        setSource('view')
      } catch (viewError) {
        if (!isViewMissing(viewError as { code?: string })) throw viewError
        rows = await loadFromMessages()
        setSource('messages')
      }
      // My own unread counts (the open conversation is read up to now first); the shared ones if that is not installed
      const mine = personalRef.current
      if (mine) {
        try {
          if (openRef.current) await mine.markRead(openRef.current)
          const p = await mine.load()
          setStarted(p.started ? new Set(p.started) : null)
          if (p.installed) rows = withPersonalUnread(rows, p.unread) as UserConversation[]
        } catch (e) {
          console.warn('Personal message counts unavailable:', e)
        }
      }
      setConversations(rows)
      setLoadError(null)
    } catch (error) {
      console.error('Error fetching conversations:', error)
      setLoadError((error as { message?: string })?.message || 'Could not load conversations')
    } finally {
      inFlight.current = false
      setIsLoading(false)
      setIsRefreshing(false)
      if (pending.current) {
        pending.current = false
        fetchConversations(false)
      }
    }
  }, [])

  const scheduleRefetch = useCallback(() => {
    if (refetchTimer.current) clearTimeout(refetchTimer.current)
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null
      fetchConversations(false)
    }, REFETCH_DEBOUNCE_MS)
  }, [fetchConversations])

  // Initial load + realtime: any change to admin_messages (new message, read receipt, delete)
  // schedules one background refresh after the burst settles.
  useEffect(() => {
    fetchConversations(true)

    const channel = supabaseAdmin
      .channel('admin_messages_dashboard')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'admin_messages' }, () => {
        scheduleRefetch()
      })
      .subscribe()

    return () => {
      if (refetchTimer.current) clearTimeout(refetchTimer.current)
      supabaseAdmin.removeChannel(channel)
    }
  }, [fetchConversations, scheduleRefetch])

  // Search across every message, not just the last one: ask the server which conversations
  // contain the text anywhere in their history. Debounced; two characters minimum.
  useEffect(() => {
    const q = searchQuery.trim()
    if (q.length < SEARCH_MIN_CHARS) {
      setRemoteMatches(null)
      setIsSearching(false)
      return
    }
    let cancelled = false
    setIsSearching(true)
    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabaseAdmin
          .from('admin_messages')
          .select('user_id')
          .is('deleted_at', null)
          .ilike('message_text', ilikePattern(q))
          .limit(2000)
        if (cancelled) return
        if (error) throw error
        setRemoteMatches(new Set((data || []).map((r) => r.user_id)))
      } catch (error) {
        if (!cancelled) {
          console.error('Message search failed:', error)
          setRemoteMatches(null)
        }
      } finally {
        if (!cancelled) setIsSearching(false)
      }
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [searchQuery])

  const changeSort = (next: string) => {
    setSort(next)
    try { localStorage.setItem(SORT_STORAGE_KEY, next) } catch { /* per-viewer convenience only */ }
  }

  const routed = useMemo(() => (routedTo ? conversations.filter((c) => isRoutedTo(c.user_id, routedTo, routeTags, started)) : conversations), [conversations, routedTo, routeTags, started])
  const visible = useMemo(
    () => sortConversations(filterConversations(routed, { query: searchQuery, unreadOnly, remoteMatches, audiences: kind === 'all' ? (audiences || null) : [kind] }), sort) as UserConversation[],
    [routed, searchQuery, unreadOnly, remoteMatches, sort, audiences, kind]
  )
  const totals = summarize(audiences ? filterConversations(routed, { audiences }) : routed)
  const deepMatches = remoteMatches
    ? visible.filter((c) => {
      const q = searchQuery.trim().toLowerCase()
      const local = [c.user_name, c.user_email, c.last_message_preview].some((v) => String(v || '').toLowerCase().includes(q))
      return !local && remoteMatches.has(c.user_id)
    }).length
    : 0

  const handleSelectConversation = (conv: UserConversation) => {
    // Two-pane: moving to another thread leaves the last one read; true up the counts.
    if (split && selectedUser && selectedUser.id !== conv.user_id) scheduleRefetch()
    if (personal) {
      setConversations((cs) => cs.map((c) => (c.user_id === conv.user_id ? { ...c, unread_count: 0 } : c)))
      personal.markRead(conv.user_id).catch(() => {})
    }
    setSelectedUser({
      id: conv.user_id,
      name: conv.user_name || 'Unknown User',
      role: conv.user_role || 'customer'
    })
  }

  const handleCloseChat = () => {
    setSelectedUser(null)
    // The chat marked messages read; true up the counts in the background.
    scheduleRefetch()
  }

  if (isLoading && conversations.length === 0) {
    return (
      <div className="p-8">
        <div className="text-center">
          <p className="text-muted-foreground">Loading conversations...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">{split ? 'Messages' : 'Admin Messages'}</h1>
        <p className="text-muted-foreground">
          {split ? 'Unified client and talent inbox.' : 'Direct messages with customers and talent'}
        </p>
      </div>

      <div className={split ? 'grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start' : ''}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Conversations
              </CardTitle>
              <CardDescription>
                {totals.total} total conversation{totals.total !== 1 ? 's' : ''}
                {totals.unread > 0 && (
                  <span className="ml-2 text-primary">
                    • {totals.unread} unread
                  </span>
                )}
                {isRefreshing && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> updating
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
          {source === 'messages' && (
            <p className="mt-2 text-xs text-amber-700">
              Conversation summary view not installed yet; loading every message instead. Run the admin_conversations migration.
            </p>
          )}
          {loadError && (
            <p className="mt-2 text-xs text-destructive">
              Could not refresh conversations: {loadError}.{' '}
              <button type="button" className="underline" onClick={() => fetchConversations(false)}>Try again</button>
            </p>
          )}
        </CardHeader>
        <CardContent>
          {/* Search + filters */}
          <div className="mb-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or any message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                aria-label="Search conversations"
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <button
                type="button"
                aria-pressed={!unreadOnly}
                onClick={() => setUnreadOnly(false)}
                className={`rounded-full border px-2.5 py-0.5 ${!unreadOnly ? 'border-primary bg-rose-50 text-primary' : 'border-slate-300 text-muted-foreground'}`}
              >
                All
              </button>
              <button
                type="button"
                aria-pressed={unreadOnly}
                onClick={() => setUnreadOnly(true)}
                className={`rounded-full border px-2.5 py-0.5 ${unreadOnly ? 'border-primary bg-rose-50 text-primary' : 'border-slate-300 text-muted-foreground'}`}
              >
                Unread{totals.unread > 0 ? ` (${totals.unread})` : ''}
              </button>
              {audiences && audiences.length > 1 && (['client', 'talent'] as const).map((k) => (
                <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(kind === k ? 'all' : k)}
                  className={`rounded-full border px-2.5 py-0.5 ${kind === k ? 'border-primary bg-rose-50 text-primary' : 'border-slate-300 text-muted-foreground'}`}>
                  {k === 'client' ? 'Clients' : 'Talent'}
                </button>
              ))}
              <label className="ml-auto flex items-center gap-1.5 text-muted-foreground">
                Sort
                <select
                  value={sort}
                  onChange={(e) => changeSort(e.target.value)}
                  className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-foreground"
                  aria-label="Sort conversations"
                >
                  {SORTS.map((s: { key: string; label: string }) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </label>
            </div>
            {deepMatches > 0 && (
              <p className="text-xs text-muted-foreground">
                {deepMatches} conversation{deepMatches !== 1 ? 's' : ''} matched in older messages.
              </p>
            )}
          </div>

          {/* Conversations List */}
          {visible.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                {searchQuery
                  ? (isSearching ? 'Searching all messages…' : 'No conversations match your search')
                  : unreadOnly ? 'Nothing unread' : 'No conversations yet'}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {visible.map((conv) => {
                  const isSelected = selectedUser?.id === conv.user_id
                  return (
                    <button
                      key={conv.user_id}
                      onClick={() => handleSelectConversation(conv)}
                      aria-current={isSelected ? 'true' : undefined}
                      className={`w-full text-left p-4 rounded-lg border hover:bg-accent transition-colors relative ${isSelected ? 'border-primary bg-rose-50/40' : ''} ${conv.unread_count > 0 ? 'border-l-4 border-l-primary' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className={`truncate ${conv.unread_count > 0 ? 'font-semibold' : 'font-medium'}`}>{conv.user_name || 'Unknown User'}</span>
                            <Badge variant="outline" className={`text-xs flex-shrink-0 ${split ? 'uppercase tracking-wide' : 'capitalize'}`}>
                              {split ? (String(conv.user_role || 'customer') === 'customer' ? 'Client' : 'Talent') : (conv.user_role || 'customer')}
                            </Badge>
                            {(routeTags?.[conv.user_id] || []).map((d) => <Badge key={d} variant="outline" className="text-[10px] flex-shrink-0 border-primary/40 text-primary">→ {ROUTE_LABEL[d] || d}</Badge>)}
                          </div>
                          <p className="text-sm text-muted-foreground mb-1 truncate">
                            {conv.user_email || ''}
                          </p>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {conv.last_message_preview || ''}
                          </p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {format(new Date(conv.last_message_at), 'MMM d, yyyy h:mm a')}
                            {conv.message_count > 0 && (
                              <span>· {conv.message_count} message{conv.message_count !== 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>

                        {/* Unread badge */}
                        {conv.unread_count > 0 && (
                          <div className="flex-shrink-0">
                            <Badge variant="default" className="bg-primary">
                              {conv.unread_count}
                            </Badge>
                          </div>
                        )}

                        {/* Read indicator */}
                        {conv.unread_count === 0 && (
                          <div className="flex-shrink-0">
                            <CheckCheck className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {split && (
        <div className="lg:sticky lg:top-4 min-h-[560px]" aria-label="Conversation thread">
          {selectedUser ? (
            <div className="space-y-2">
            {paneHeader?.({ id: selectedUser.id, name: selectedUser.name, role: selectedUser.role })}
            <AdminChatPanel
              key={selectedUser.id}
              embedded
              userId={selectedUser.id}
              userName={selectedUser.name}
              userRole={selectedUser.role}
              onClose={handleCloseChat}
              onPickMessage={chatProps?.onPickMessage ? (m) => chatProps.onPickMessage!({ ...m, userId: selectedUser.id }) : undefined}
              highlightId={chatProps?.highlightId}
              markers={chatProps?.markers}
            />
            </div>
          ) : (
            <div className="flex h-[560px] items-center justify-center rounded-xl border bg-card text-sm text-muted-foreground">Select a conversation to view the thread.</div>
          )}
        </div>
      )}
      </div>

      {/* Admin Chat Panel */}
      {!split && selectedUser && (
        <AdminChatPanel
          userId={selectedUser.id}
          userName={selectedUser.name}
          userRole={selectedUser.role}
          onClose={handleCloseChat}
        />
      )}
    </div>
  )
}
